import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, Clock, X, ArrowRight, CheckCircle, Zap } from 'lucide-react';

const WEEKDAY_LABELS = ['Ned', 'Pon', 'Tor', 'Sre', 'Čet', 'Pet', 'Sob'];
const MONTH_LABELS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'avg', 'sep', 'okt', 'nov', 'dec'];

function formatSlot(dateStr) {
  const d = new Date(dateStr);
  return {
    weekday: WEEKDAY_LABELS[d.getDay()],
    day: d.getDate(),
    month: MONTH_LABELS[d.getMonth()],
  };
}

/**
 * Floating "Pohitri rezervacijo" button — replaces the old AI assistant.
 * Click → expands a glass popup showing the next 3 available slots.
 * Each slot links to /?book=true&date=&time=&therapist= (auto-skips calendar in modal).
 */
const FloatingBookingButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const popupRef = useRef(null);

  // Re-fetch slots every time the popup is opened (avoids showing stale/booked slots)
  const fetchSlots = () => {
    setLoading(true);
    axios.get('/api/next-available-slots?service_id=1&limit=3')
      .then(res => { setSlots(res.data?.slots || []); setLoading(false); })
      .catch(() => { setSlots([]); setLoading(false); });
  };

  // Click outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (isOpen && popupRef.current && !popupRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [isOpen]);

  const toggle = () => {
    if (!isOpen) fetchSlots();
    setIsOpen(prev => !prev);
  };

  return (
    <div ref={popupRef}>
      {/* Floating Button */}
      <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50">
        <button
          onClick={toggle}
          aria-label="Pohitri rezervacijo"
          className={`relative w-14 h-14 md:w-16 md:h-16 rounded-full shadow-2xl transition-all duration-300 group ${
            isOpen
              ? 'bg-slate-700'
              : 'bg-gradient-to-br from-[#f87171] to-[#ef4444] hover:from-[#ef4444] hover:to-[#dc2626] hover:scale-110'
          }`}
          data-testid="floating-booking-btn"
        >
          {isOpen ? (
            <X className="w-6 h-6 md:w-7 md:h-7 text-white mx-auto" />
          ) : (
            <>
              {/* Pulse ring */}
              <span className="absolute inset-0 rounded-full bg-[#f87171] opacity-40 animate-ping" />
              {/* Calendar icon */}
              <Calendar className="relative w-6 h-6 md:w-7 md:h-7 text-white mx-auto" strokeWidth={2.5} />
              {/* Green availability dot */}
              <span className="absolute bottom-1 right-1 w-3 h-3 md:w-3.5 md:h-3.5 bg-emerald-400 border-2 border-white rounded-full shadow-md" />
            </>
          )}
        </button>
      </div>

      {/* Popup Window */}
      <div
        className={`fixed bottom-20 md:bottom-24 right-4 md:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[340px] bg-gradient-to-br from-[#1a365d] to-[#0f1e36] rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        data-testid="floating-booking-popup"
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-amber-300" />
            <h3 className="text-white font-bold tracking-tight">Pohitri rezervacijo</h3>
          </div>
          <p className="text-white/70 text-xs leading-relaxed">
            Najbližji prosti termini. Rezerviraj v 1 kliku — preskočimo izbiranje datuma.
          </p>
        </div>

        {/* Slots */}
        <div className="p-3 space-y-2">
          {loading && (
            <>
              {[1, 2, 3].map(i => <div key={i} className="bg-white/10 animate-pulse h-14 rounded-xl" />)}
            </>
          )}

          {!loading && slots.length === 0 && (
            <Link
              to="/?book=true"
              onClick={() => setIsOpen(false)}
              className="block text-center bg-[#f87171] hover:bg-[#ef4444] text-white font-semibold rounded-xl py-3 px-4 transition"
              data-testid="floating-fallback-book"
            >
              Naroči se online
            </Link>
          )}

          {!loading && slots.map((slot, idx) => {
            const f = formatSlot(slot.date);
            const bookingUrl = `/?book=true&date=${slot.date}&time=${slot.time}${slot.therapist_id ? `&therapist=${slot.therapist_id}` : ''}`;
            return (
              <Link
                key={`${slot.date}-${slot.time}-${idx}`}
                to={bookingUrl}
                onClick={() => setIsOpen(false)}
                className="group flex items-center gap-3 bg-white/10 hover:bg-white/20 rounded-xl px-3 py-2.5 transition-all border border-white/10 hover:border-amber-300/60"
                data-testid={`floating-slot-${idx}`}
              >
                {/* Date pill */}
                <div className="flex flex-col items-center bg-white/10 rounded-lg px-2 py-1 min-w-[50px]">
                  <span className="text-[10px] uppercase tracking-wider text-amber-200 font-bold">{f.weekday}</span>
                  <span className="text-xl font-extrabold text-white leading-none">{f.day}</span>
                  <span className="text-[10px] text-white/60">{f.month}</span>
                </div>
                {/* Time + therapist */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 font-bold text-white">
                    <Clock className="w-3.5 h-3.5 text-amber-300" />
                    {slot.time}
                  </div>
                  <div className="text-[11px] text-white/60 truncate">{slot.therapist_name}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-amber-300 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 pt-2 border-t border-white/10 space-y-1.5">
          <Link
            to="/?book=true"
            onClick={() => setIsOpen(false)}
            className="block text-center text-amber-200 hover:text-amber-100 text-xs font-semibold underline-offset-4 hover:underline mb-1"
          >
            Več terminov →
          </Link>
          <div className="flex items-center justify-between gap-2 text-[10px] text-white/60">
            <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> Brez napotnice</div>
            <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-400" /> Potrditev v 5 min</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingBookingButton;
